import { useRef, useState, useEffect } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Simple voice navigation for the Readiness page.
 * Commands: "search <term>", "read my score", "open <category>", "go to dashboard", "help".
 */
export default function VoiceNav({ categories = [], onSearch, onReadScore, onOpenCategory, onGoDashboard, speak }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = 'en-US';
    rec.onresult = (e) => handle(e.results[0][0].transcript.toLowerCase().trim());
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    return () => { try { rec.abort(); } catch { /* noop */ } };
  }, [categories]); // eslint-disable-line

  const handle = (t) => {
    if (!t) return;
    if (t.includes('help')) {
      const msg = 'You can say: search followed by a term, read my score, open a category name, or go to dashboard.';
      speak?.(msg); toast('Voice commands', { description: msg }); return;
    }
    if (t.includes('score')) { onReadScore?.(); return; }
    if (t.includes('dashboard')) { speak?.('Opening your dashboard.'); onGoDashboard?.(); return; }
    const searchMatch = t.match(/(?:search|find|look for)\s+(.*)/);
    if (searchMatch) { const q = searchMatch[1].trim(); speak?.(`Searching for ${q}.`); onSearch?.(q); return; }
    const openMatch = t.match(/(?:open|show|go to)\s+(.*)/);
    if (openMatch) {
      const term = openMatch[1].trim();
      const cat = categories.find((c) => c.name.toLowerCase().includes(term) || term.includes(c.name.toLowerCase()));
      if (cat) { speak?.(`Opening ${cat.name}.`); onOpenCategory?.(cat.id); return; }
    }
    // default: treat whole phrase as a search
    speak?.(`Searching for ${t}.`);
    onSearch?.(t);
  };

  const toggle = () => {
    if (!supported) { toast.error('Voice navigation is not supported in this browser.'); return; }
    if (listening) { recRef.current?.stop(); setListening(false); return; }
    try {
      recRef.current?.start();
      setListening(true);
      speak?.('Listening. Try: read my score, or search for a term.');
    } catch { setListening(false); }
  };

  return (
    <button onClick={toggle} data-testid="readiness-voice-button"
      aria-label={listening ? 'Stop voice navigation' : 'Start voice navigation'}
      title={supported ? 'Voice navigation' : 'Voice not supported in this browser'}
      className={`p-2 rounded-full ${listening ? 'bg-[#FBE9E7] text-[#C0492E] animate-pulse' : 'text-[#8D8880] hover:bg-[#F5F4F2]'}`}>
      {listening ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
    </button>
  );
}
